import React, { useState } from "react";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import ContentWrapper from "../../components/layout/ContentWrapper";
import {
  Filter,
  FlexContent,
  PageHeading,
  SearchBox,
  useAuth,
} from "../../components";
import ServiceObjectsTable from "./components/ServiceObjectsTable";
import { ServiceObjectTableQuery } from "./models";
import {
  getTableFiltersFromCodebooks,
  getTableParsedFiltersForQuery,
} from "../../helpers/tableHelpers";
import { useGetCodebooksQuery } from "../../common/queries";
import { SelectItem } from "../../components/select/Select";
import { useCustomers } from "../../helpers/customersHelper";

const defaultTableQuery: ServiceObjectTableQuery = {
  page: 1,
  pageSize: 10,
};

const ServiceObjectsPage: React.FC = (): React.ReactElement => {
  const { tServiceObject, tCommon, t } = useCustomTranslation();
  const codebooksQuery = useGetCodebooksQuery();
  const { user } = useAuth();

  const { loadCustomersForFilter } = useCustomers();

  const [queryParams, setQueryParams] =
    useState<ServiceObjectTableQuery>(defaultTableQuery);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, SelectItem[]>
  >({});

  const handleSearch = (searchPhrase: string) => {
    setQueryParams({ ...queryParams, searchPhrase });
  };

  const handleFilter = (selectedFilters: Record<string, SelectItem[]>) => {
    const parsedFilters = getTableParsedFiltersForQuery(selectedFilters);

    setQueryParams({
      searchPhrase: queryParams.searchPhrase,
      ...defaultTableQuery,
      ...parsedFilters,
    });
    setSelectedFilters(selectedFilters);
  };

  return (
    <ContentWrapper htmlTitle={tCommon("menu_serviceObjects")}>
      <FlexContent className="mb-4">
        <PageHeading title={tServiceObject("table_heading")} />
        <FlexContent className="gap-4">
          <Filter
            selectedFitlers={selectedFilters}
            updateFilters={handleFilter}
            filters={[
              ...getTableFiltersFromCodebooks(
                [
                  { key: "categoryId", source: "serviceObjectCategories" },
                  {
                    key: "manufacturerId",
                    source: "serviceObjectManufacturers",
                  },
                ],
                t,
                codebooksQuery.data,
              ),
              {
                key: "owningCustomerId",
                label: tServiceObject("owning_customer"),
                isAsync: true,
                options: [],
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                loadOptions: (inputValue, cb) =>
                  loadCustomersForFilter(inputValue, cb, "isDeviceOwner"),
                hidden: !user?.hasServiceObjectsWithOwner,
              },
              {
                key: "customerId",
                label: tServiceObject("operator_customer"),
                isAsync: true,
                options: [],
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                loadOptions: (inputValue, cb) =>
                  loadCustomersForFilter(inputValue, cb, "isDeviceUser"),
                hidden: !user?.ownsServiceObjects,
              },
              {
                key: "isActive",
                label: tServiceObject("state"),
                options: [
                  {
                    label: t("active"),
                    value: "true",
                  },
                  {
                    label: t("inactive"),
                    value: "false",
                  },
                ],
              },
            ]}
          />
          <SearchBox onSearch={handleSearch} />
        </FlexContent>
      </FlexContent>
      <ServiceObjectsTable
        queryParams={queryParams}
        setQueryParams={setQueryParams}
      />
    </ContentWrapper>
  );
};

export default ServiceObjectsPage;
